import type Phaser from "phaser";

import type { ArenaConfig } from "../config/gameplay-config";
import {
  getArenaBackgroundConfig,
  type ArenaBackgroundId,
} from "../config/arena-background-config";
import type {
  SeasonalArenaDecoration,
  SeasonalArenaPalette,
  SeasonalChallengeDescriptor,
} from "../config/seasonal-challenge-config";

export interface SeasonalArenaRenderOptions {
  readonly highContrast: boolean;
  readonly reducedMotion: boolean;
}

const standardSeasonalOptions: SeasonalArenaRenderOptions = {
  highContrast: false,
  reducedMotion: false,
};

const drawSeasonalDecoration = (
  graphics: Phaser.GameObjects.Graphics,
  arena: ArenaConfig,
  decoration: SeasonalArenaDecoration,
  palette: SeasonalArenaPalette,
): void => {
  const color = decoration.color ?? palette.decorationColor;
  const width = decoration.widthRatio * arena.width;
  const height = decoration.heightRatio * arena.height;
  const x = decoration.xRatio * arena.width;
  const y = decoration.yRatio * arena.height;
  graphics.fillStyle(color, decoration.alpha);
  if (decoration.kind === "ellipse") {
    graphics.fillEllipse(x, y, width, height);
    return;
  }
  if (decoration.kind === "rectangle") {
    graphics.fillRect(x - width / 2, y - height / 2, width, height);
    return;
  }
  if (decoration.kind === "mist") {
    graphics.fillEllipse(x - width * 0.24, y, width * 0.66, height);
    graphics.fillEllipse(x + width * 0.24, y, width * 0.66, height * 0.82);
    return;
  }
  if (decoration.kind === "crescent") {
    graphics.fillEllipse(x, y, width, height);
    graphics.fillStyle(palette.fieldColor, 1);
    graphics.fillEllipse(
      x + width * 0.2,
      y - height * 0.08,
      width * 0.78,
      height * 0.78,
    );
    return;
  }
  if (decoration.kind === "pumpkin") {
    graphics.fillEllipse(x - width * 0.18, y, width * 0.58, height);
    graphics.fillEllipse(x + width * 0.18, y, width * 0.58, height);
    graphics.fillEllipse(x, y, width * 0.56, height);
    graphics.fillStyle(palette.boundaryOutlineColor, decoration.alpha);
    graphics.fillRect(
      x - width * 0.06,
      y - height * 0.66,
      width * 0.12,
      height * 0.2,
    );
    return;
  }
  graphics.fillEllipse(x, y, width * 0.2, height * 0.72);
  graphics.fillTriangle(
    x,
    y,
    x - width / 2,
    y - height * 0.35,
    x - width * 0.12,
    y + height * 0.3,
  );
  graphics.fillTriangle(
    x,
    y,
    x + width / 2,
    y - height * 0.35,
    x + width * 0.12,
    y + height * 0.3,
  );
};

export const drawArenaBackground = (
  graphics: Phaser.GameObjects.Graphics,
  arena: ArenaConfig,
  id: ArenaBackgroundId,
  seasonal?: SeasonalChallengeDescriptor,
  options: SeasonalArenaRenderOptions = standardSeasonalOptions,
): void => {
  const selected = getArenaBackgroundConfig(id);
  const style =
    seasonal === undefined
      ? selected
      : options.highContrast
        ? seasonal.arena.highContrastPalette
        : seasonal.arena.palette;
  graphics.clear();
  graphics.fillStyle(style.backgroundColor, 1);
  graphics.fillRect(0, 0, arena.width, arena.height);
  graphics.fillStyle(style.boundaryColor, 1);
  graphics.fillRect(
    arena.boundaryInset / 2,
    arena.boundaryInset / 2,
    arena.width - arena.boundaryInset,
    arena.height - arena.boundaryInset,
  );
  graphics.fillStyle(style.fieldColor, 1);
  graphics.fillRect(
    arena.boundaryInset,
    arena.boundaryInset,
    arena.width - arena.boundaryInset * 2,
    arena.height - arena.boundaryInset * 2,
  );
  if (seasonal === undefined) {
    for (const accent of selected.accents) {
      graphics.fillStyle(accent.color, accent.alpha);
      graphics.fillEllipse(
        accent.xRatio * arena.width,
        accent.yRatio * arena.height,
        accent.widthRatio * arena.width,
        accent.heightRatio * arena.height,
      );
    }
  } else {
    const seasonalPalette = options.highContrast
      ? seasonal.arena.highContrastPalette
      : seasonal.arena.palette;
    for (const decoration of seasonal.arena.decorations) {
      if (options.reducedMotion && decoration.reducedMotion === "hidden") {
        continue;
      }
      drawSeasonalDecoration(graphics, arena, decoration, seasonalPalette);
    }
  }
  graphics.lineStyle(8, style.boundaryOutlineColor, 1);
  graphics.strokeRect(
    arena.boundaryInset,
    arena.boundaryInset,
    arena.width - arena.boundaryInset * 2,
    arena.height - arena.boundaryInset * 2,
  );
};
